import { type ChangeEvent, type DragEvent, useEffect, useRef, useState } from 'react';

interface UseFileUploadOptions {
  accept?: string;
  multiple?: boolean;
  maxBytes?: number;
}

interface FilePreview {
  file: File;
  url: string;
}

interface UseFileUploadResult {
  files: File[];
  previews: FilePreview[];
  inputRef: React.RefObject<HTMLInputElement | null>;
  openPicker: () => void;
  reset: () => void;
  inputProps: {
    type: 'file';
    accept?: string;
    multiple?: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    ref: React.RefObject<HTMLInputElement | null>;
    style: React.CSSProperties;
  };
  dropZoneProps: {
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
    'data-dragging': boolean;
  };
  isDragging: boolean;
  error?: string;
}

export function useFileUpload(opts: UseFileUploadOptions = {}): UseFileUploadResult {
  const { accept, multiple = false, maxBytes } = opts;
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setDragging] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const previews = files
    .filter((f) => f.type.startsWith('image/'))
    .map((file) => ({ file, url: URL.createObjectURL(file) }));

  // biome-ignore lint/correctness/useExhaustiveDependencies: previews is derived from `files` and recomputed each render; only the cleanup needs the latest urls.
  useEffect(() => {
    return () => {
      for (const p of previews) URL.revokeObjectURL(p.url);
    };
  }, [files]);

  const ingest = (incoming: FileList | null) => {
    if (!incoming) return;
    const list = Array.from(incoming);
    if (maxBytes) {
      const tooBig = list.find((f) => f.size > maxBytes);
      if (tooBig) {
        setError(`Bestand "${tooBig.name}" overschrijdt ${Math.round(maxBytes / 1024 / 1024)} MB.`);
        return;
      }
    }
    setError(undefined);
    setFiles(multiple ? list : list.slice(0, 1));
  };

  return {
    files,
    previews,
    inputRef,
    openPicker: () => inputRef.current?.click(),
    reset: () => {
      setFiles([]);
      setError(undefined);
      if (inputRef.current) inputRef.current.value = '';
    },
    inputProps: {
      type: 'file',
      accept,
      multiple,
      onChange: (e: ChangeEvent<HTMLInputElement>) => ingest(e.target.files),
      ref: inputRef,
      style: { display: 'none' },
    },
    dropZoneProps: {
      onDragOver: (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        setDragging(true);
      },
      onDragLeave: (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        setDragging(false);
      },
      onDrop: (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        setDragging(false);
        ingest(e.dataTransfer.files);
      },
      'data-dragging': isDragging,
    },
    isDragging,
    error,
  };
}
